import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Table, Button, Tag, Modal, Input, message, Space, Tooltip, Form, Select, DatePicker, Radio, Spin, TimePicker } from 'antd'
import { EyeOutlined, CheckOutlined, CloseOutlined, SearchOutlined, UserAddOutlined, MedicineBoxOutlined, SendOutlined, CalendarOutlined } from '@ant-design/icons'
import Layout from '../Home'
import http from '../../services/httpServiceHealthCare'
import httpEver from '../../services/httpServiceEver'
import { Formik, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import rawDatabase from '../../asset/raw_database.json'
import useHNConverter from '../../hooks/DataConverter/useHNConverter/useHNConverter'
import moment from 'moment'
import 'moment/locale/th'
import jwtDecode from 'jwt-decode'
import { find } from 'lodash'

const { TextArea } = Input
const { Option } = Select

const Container = styled.div`
  padding: 20px;
  
  .header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 10px;
    }
  }
  
  .search-section {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    
    @media (max-width: 768px) {
      flex-direction: column;
    }
  }
  
  .table-container {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }
  
  .status-pending {
    background-color: #faad14;
    color: white;
  }
  
  .status-approved {
    background-color: #52c41a;
    color: white;
  }
  
  .status-sent_to_pharmacy {
    background-color: #1890ff;
    color: white;
  }
  
  .status-medicine_delivered {
    background-color: #722ed1;
    color: white;
  }
`

const PatientRegistrationList = (props) => {
    const [registrations, setRegistrations] = useState([])
    const [loading, setLoading] = useState(false)
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    })
    const [searchText, setSearchText] = useState('')
    const [selectedRecord, setSelectedRecord] = useState(null)
    const [completeRegistrationModalVisible, setCompleteRegistrationModalVisible] = useState(false)
    const [sendToPharmacyModalVisible, setSendToPharmacyModalVisible] = useState(false)
    const [pharmacyNotes, setPharmacyNotes] = useState('')
    const [patientDataFromHIE, setPatientDataFromHIE] = useState(null)
    const [loadingPatientData, setLoadingPatientData] = useState(false)
    const [bookingModalVisible, setBookingModalVisible] = useState(false)
    const [bookingDate, setBookingDate] = useState(null)
    const [bookingTime, setBookingTime] = useState(null)
    const [bookingNotes, setBookingNotes] = useState('')
    const [loadingBooking, setLoadingBooking] = useState(false)
    const [patientId, setPatientId] = useState(null)

    // สำหรับการเลือกแผนก แพทย์ และช่วงเวลา
    const [doctorTypes, setDoctorTypes] = useState([])
    const [otherTypes, setOtherTypes] = useState([])
    const [selectedDepartmentType, setSelectedDepartmentType] = useState(null) // 'doctorTypes' หรือ 'otherTypes'
    const [selectedDepartmentId, setSelectedDepartmentId] = useState(null)
    const [doctors, setDoctors] = useState([])
    const [selectedDoctorId, setSelectedDoctorId] = useState(null)
    const [selectedPractitioner, setSelectedPractitioner] = useState(null)
    const [availableDays, setAvailableDays] = useState([])
    const [selectedDate, setSelectedDate] = useState(null)
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(null)
    const [loadingDepartments, setLoadingDepartments] = useState(false)
    const [loadingDoctors, setLoadingDoctors] = useState(false)
    const [loadingTimeSlots, setLoadingTimeSlots] = useState(false)

    const [provinces, setProvinces] = useState([])
    const [amphoes, setAmphoes] = useState([])
    const [districts, setDistricts] = useState([])
    const [selectedProvince, setSelectedProvince] = useState('')
    const [selectedAmphoe, setSelectedAmphoe] = useState('')
    const [selectedDistrict, setSelectedDistrict] = useState('')
    const [postalCode, setPostalCode] = useState('')

    useEffect(() => {
        fetchRegistrations()
        loadProvinces()
        fetchDepartments()
    }, [])

    useEffect(() => {
        if (patientDataFromHIE && patientDataFromHIE.data && patientDataFromHIE.data.address) {
            const addressData = patientDataFromHIE.data.address

            if (addressData.chwpart) {
                setSelectedProvince(addressData.chwpart)
                const filteredAmphoes = rawDatabase
                    .filter(item => item.province === addressData.chwpart)
                    .map(item => item.amphoe)
                const uniqueAmphoes = [...new Set(filteredAmphoes)]
                setAmphoes(uniqueAmphoes)
            }

            if (addressData.amppart) {
                setSelectedAmphoe(addressData.amppart)
                const filteredDistricts = rawDatabase
                    .filter(item => item.amphoe === addressData.amppart)
                    .map(item => item.district)
                const uniqueDistricts = [...new Set(filteredDistricts)]
                setDistricts(uniqueDistricts)
            }

            if (addressData.tmbpart) {
                setSelectedDistrict(addressData.tmbpart)
                const foundItem = rawDatabase.find(item =>
                    item.province === addressData.chwpart &&
                    item.amphoe === addressData.amppart &&
                    item.district === addressData.tmbpart
                )
                if (foundItem) {
                    setPostalCode(foundItem.zipcode.toString())
                }
            }
        }
    }, [patientDataFromHIE])

    const loadProvinces = () => {
        const uniqueProvinces = [...new Set(rawDatabase.map(item => item.province))]
        setProvinces(uniqueProvinces)
    }

    const handleProvinceChange = (value) => {
        setSelectedProvince(value)
        const filteredAmphoes = rawDatabase
            .filter(item => item.province === value)
            .map(item => item.amphoe)
        const uniqueAmphoes = [...new Set(filteredAmphoes)]
        setAmphoes(uniqueAmphoes)
        setSelectedAmphoe('')
        setSelectedDistrict('')
        setPostalCode('')
    }

    const handleAmphoeChange = (value) => {
        setSelectedAmphoe(value)
        const filteredDistricts = rawDatabase
            .filter(item => item.amphoe === value)
            .map(item => item.district)
        const uniqueDistricts = [...new Set(filteredDistricts)]
        setDistricts(uniqueDistricts)
        setSelectedDistrict('')
        setPostalCode('')
    }

    const handleDistrictChange = (value) => {
        setSelectedDistrict(value)
        const foundItem = rawDatabase.find(item =>
            item.province === selectedProvince &&
            item.amphoe === selectedAmphoe &&
            item.district === value
        )
        if (foundItem) {
            setPostalCode(foundItem.zipcode.toString())
        }
    }

    const fetchRegistrations = async (page = 1, search = '') => {
        setLoading(true)
        try {
            const params = {
                filter: JSON.stringify({
                    limit: pagination.pageSize,
                    skip: (page - 1) * pagination.pageSize,
                    order: 'createdAt DESC'
                })
            }

            if (search) {
                params.filter = JSON.stringify({
                    ...JSON.parse(params.filter),
                    where: {
                        or: [
                            { firstname: { like: `%${search}%` } },
                            { lastname: { like: `%${search}%` } },
                            { hnId: { like: `%${search}%` } }
                        ]
                    }
                })
            }

            const response = await http.get('/PatientRegistrations', { params })

            setRegistrations(response.data)
            setPagination(prev => ({
                ...prev,
                current: page,
                total: response.data.length
            }))
        } catch (error) {
            console.error('Error fetching registrations:', error)
            message.error('เกิดข้อผิดพลาดในการโหลดข้อมูล')
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = (value) => {
        setSearchText(value)
        fetchRegistrations(1, value)
    }

    const handleTableChange = (pagination) => {
        fetchRegistrations(pagination.current, searchText)
    }

    const handleCompleteRegistration = async (record) => {
        setSelectedRecord(record)
        setCompleteRegistrationModalVisible(true)
        setPatientDataFromHIE(null)

        if (record.cId) {
            await fetchPatientDataFromHIE(record.cId)
        }
    }

    const fetchPatientDataFromHIE = async (cId) => {
        setLoadingPatientData(true)
        try {
            const response = await http.get(`/UserInfos/checkUserPatientByVerifiedFromHIE`, {
                params: { cId }
            })

            if (response.data) {
                setPatientDataFromHIE(response.data)
            }
        } catch (error) {
            console.error('Error fetching patient data from HIE:', error)
            message.warning('ไม่สามารถดึงข้อมูลจาก HIE ได้')
        } finally {
            setLoadingPatientData(false)
        }
    }

    const convertDateToBE = (dateString) => {
        if (!dateString) return { day: '', month: '', year: '' }

        try {
            const dateParts = dateString.split('-')
            if (dateParts.length === 3) {
                const yearCE = parseInt(dateParts[0])
                const month = parseInt(dateParts[1])
                const day = parseInt(dateParts[2])
                const yearBE = yearCE + 543

                return {
                    day: day.toString(),
                    month: month.toString(),
                    year: yearBE.toString()
                }
            }

            const date = new Date(dateString)
            if (!isNaN(date.getTime())) {
                const day = date.getDate()
                const month = date.getMonth() + 1
                const year = date.getFullYear() + 543

                return {
                    day: day.toString(),
                    month: month.toString(),
                    year: year.toString()
                }
            }
        } catch (error) {
            console.error('Error converting date:', error)
        }

        return { day: '', month: '', year: '' }
    }

    const convertGender = (sex, sexName) => {
        if (sex === '1' || sexName === 'ชาย') {
            return 'male'
        } else if (sex === '2' || sexName === 'หญิง') {
            return 'female'
        }
        return 'male'
    }

    const getRawHN = (hnValue) => {
        if (!hnValue) return null

        let hn = null
        if (Array.isArray(hnValue)) {
            hn = hnValue[0]?.hn || hnValue[0]
        } else if (typeof hnValue === 'object' && hnValue.hn) {
            hn = hnValue.hn
        } else {
            hn = hnValue
        }

        if (!hn) return null

        return hn.toString()
    }

    const getFormattedHN = (hnValue) => {
        if (!hnValue) return ''

        const rawHN = getRawHN(hnValue)
        if (!rawHN) return ''

        const { convertHN } = useHNConverter(rawHN)
        return convertHN()
    }

    const handleSendToPharmacy = (record) => {
        setSelectedRecord(record)
        setSendToPharmacyModalVisible(true)
        setPharmacyNotes('')
    }

    const handleCompleteRegistrationSubmit = async (values) => {
        try {
            let rawHN = getRawHN(patientDataFromHIE?.data?.hn || patientDataFromHIE?.hn) || selectedRecord?.hnId

            if (!rawHN && values.hnId) {
                if (values.hnId.includes('/')) {
                    const { revertHN } = useHNConverter(values.hnId)
                    rawHN = revertHN()
                } else {
                    rawHN = values.hnId
                }
            }

            if (!rawHN) {
                rawHN = values.hnId
            }

            const formattedMonth = values.month.toString().padStart(2, '0')
            const formattedDay = values.day.toString().padStart(2, '0')
            const formattedBirthDate = `${values.year}-${formattedMonth}-${formattedDay}`

            const additionalData = {
                email: `${values.cId}@everapp.io`,
                firstname: values.firstname,
                lastname: values.lastname,
                password: values.password,
                emailverified: true,
                gender: values.gender,
                logistic: "not available",
                birthDate: formattedBirthDate,
                hnId: rawHN,
                cId: values.cId,
                mobileNumber: values.mobileNumber,
                address: {
                    address: values.address,
                    address2: selectedDistrict,
                    area: selectedAmphoe,
                    province: selectedProvince,
                    postalCode: postalCode
                },
                verified: true
            }

            const approveResponse = await http.post(`/PatientRegistrations/${selectedRecord.id}/approveRegistration`, {
                additionalData: additionalData,
                notes: 'สมัครเสร็จสิ้นโดยเจ้าหน้าที่ - กรอกข้อมูลครบถ้วน'
            })

            // หลังจาก approveRegistration สำเร็จ ต้อง login ที่ OMA Backend เพื่อได้ token
            // แล้วเอา token ไปยิงที่ users/loginWithToken เพื่อสร้าง AppUserIdentity
            try {
                // Login ที่ OMA Backend (Health Care Backend) เพื่อได้ JWT token
                // POST /api/Users/login ใช้ email และ password ที่เพิ่งสร้าง
                const omaLoginResponse = await http.post('/Users/login', {
                    email: additionalData.email,
                    password: additionalData.password
                })


                if (omaLoginResponse.data && omaLoginResponse.data.id) {
                    const omaToken = omaLoginResponse.data.id

                    // เอา token ไป login ที่ Ever Backend เพื่อสร้าง AppUserIdentity และเชื่อมโยง patient
                    await httpEver.post('/Users/loginWithToken', {
                        jwtToken: omaToken
                    })
                }
            } catch (loginError) {
                console.error('Error logging in with OMA token:', loginError)
                // ไม่แสดง error message เพราะการสมัครสำเร็จแล้ว แค่ login ไม่สำเร็จ
            }

            message.success('สมัครผู้ป่วยเรียบร้อยแล้ว')
            setCompleteRegistrationModalVisible(false)
            fetchRegistrations(pagination.current, searchText)
        } catch (error) {
            console.error('Error completing registration:', error)
            message.error('เกิดข้อผิดพลาดในการสมัครผู้ป่วย')
        }
    }

    const handleSendToPharmacySubmit = async () => {
        try {
            await http.post(`/PatientRegistrations/${selectedRecord.id}/sendToPharmacy`, {
                notes: pharmacyNotes
            })

            message.success('ส่งผู้ป่วยไปยัง Pharmacy Emergency เรียบร้อยแล้ว')
            setSendToPharmacyModalVisible(false)
            fetchRegistrations(pagination.current, searchText)
        } catch (error) {
            console.error('Error sending to pharmacy:', error)
            message.error('เกิดข้อผิดพลาดในการส่งไปยัง Pharmacy')
        }
    }

    const fetchDepartments = async () => {
        setLoadingDepartments(true)
        try {
            const [doctorTypesRes, otherTypesRes] = await Promise.all([
                httpEver.get('/DoctorTypes'),
                httpEver.get('/OtherTypes')
            ])
            setDoctorTypes(doctorTypesRes.data || [])
            setOtherTypes(otherTypesRes.data || [])
        } catch (error) {
            console.error('Error fetching departments:', error)
            message.error('ไม่สามารถดึงข้อมูลแผนกได้')
        } finally {
            setLoadingDepartments(false)
        }
    }

    const fetchDoctorsByDepartment = async (departmentType, departmentId) => {
        setLoadingDoctors(true)
        setDoctors([])
        setSelectedDoctorId(null)
        setSelectedPractitioner(null)
        setAvailableDays([])
        setSelectedTimeSlot(null)
        try {
            let response
            if (departmentType === 'doctorTypes') {
                response = await httpEver.get(`/DoctorTypes/${departmentId}/appUsers?filter[order]=employeeId`)
            } else {
                response = await httpEver.get(`/OtherTypes/getAppUserByOtherTypeId?otherTypeId=${departmentId}`)
            }
            setDoctors(response.data || [])
        } catch (error) {
            console.error('Error fetching doctors:', error)
            message.error('ไม่สามารถดึงข้อมูลแพทย์ได้')
        } finally {
            setLoadingDoctors(false)
        }
    }

    const fetchDoctorAvailableTimes = async (doctorId) => {
        setLoadingTimeSlots(true)
        setAvailableDays([])
        setSelectedDate(null)
        setSelectedTimeSlot(null)
        try {
            // ดึงข้อมูลช่วงเวลาว่างของแพทย์
            const response = await httpEver.get('/doctorAvailableTimes/filterType', {
                params: {
                    doctorId: doctorId,
                    type: 'weekly'
                }
            })

            if (!response.data || !Array.isArray(response.data.time)) {
                throw new Error('Invalid available time payload')
            }

            const slotMinute = Number(response.data.minute) || 30
            const sortAvailableTimes = [...response.data.time].sort((a, b) => (a?.[0] ?? 0) - (b?.[0] ?? 0))
            const availableDaysData = []

            sortAvailableTimes.forEach((timeRange) => {
                const timeStart = timeRange[0]
                const timeEnd = timeRange[1]

                // สร้าง timePeriods (ช่วงเวลาทั้งหมด)
                const timePeriods = []
                let start = timeStart
                while (start <= timeEnd) {
                    timePeriods.push(start)
                    start += slotMinute
                }

                const dayMoment = moment()
                    .startOf('isoWeek')
                    .add(timeStart, 'minutes')
                    .locale('th')

                const dayOfWeek = dayMoment.day() === 0 ? 6 : dayMoment.day() - 1 // 0-6 (Monday = 0)

                const dayData = {
                    value: dayOfWeek.toString(), // เก็บเป็น string สำหรับการ filter
                    label: dayMoment.format('dddd'),
                    dayOfWeek: dayOfWeek,
                    timePeriods: timePeriods,
                }

                availableDaysData.push(dayData)
            })

            setAvailableDays(availableDaysData)
        } catch (error) {
            console.error('Error fetching available times:', error)
            message.error('ไม่สามารถดึงข้อมูลช่วงเวลาว่างได้')
        } finally {
            setLoadingTimeSlots(false)
        }
    }

    const fetchAppUserIdentity = async (everOmaId) => {
        try {
            const response = await httpEver.get(`/AppUserIdentities?filter[where][everOmaId]=${everOmaId}`)
            if (response.data && response.data.length > 0) {
                const appUserId = response.data[response.data.length - 1].appUserId
                return appUserId
            }
            return null
        } catch (error) {
            console.error('Error fetching AppUserIdentity:', error)
            return null
        }
    }

    const handleCreateBooking = async (record) => {
        setSelectedRecord(record)
        setBookingModalVisible(true)
        setBookingDate(null)
        setBookingTime(null)
        setBookingNotes('')
        setPatientId(null)
        setSelectedDepartmentType(null)
        setSelectedDepartmentId(null)
        setDoctors([])
        setSelectedDoctorId(null)
        setSelectedPractitioner(null)
        setAvailableDays([])
        setSelectedTimeSlot(null)

        // ดึง patientId จาก cId
        if (record.cId) {
            try {
                const response = await http.get(`/UserInfos/userInfoByPatientCid`, {
                    params: { cId: record.cId }
                })
                if (response.data && response.data.userId) {
                    const userId = response.data.userId

                    // ดึง everOmaId จาก AppUserIdentity
                    const appUserId = await fetchAppUserIdentity(userId)
                    if (appUserId) {
                        setPatientId(appUserId)
                    }
                } else {
                    message.warning('ไม่พบข้อมูลผู้ป่วยในระบบ กรุณาตรวจสอบ')
                }
            } catch (error) {
                console.error('Error fetching patient ID:', error)
                message.warning('ไม่สามารถดึงข้อมูลผู้ป่วยได้')
            }
        }
    }

    const handleDepartmentTypeChange = (type) => {
        setSelectedDepartmentType(type)
        setSelectedDepartmentId(null)
        setDoctors([])
        setSelectedDoctorId(null)
        setAvailableDays([])
        setSelectedDate(null)
        setSelectedTimeSlot(null)
    }

    const handleDepartmentChange = (departmentId) => {
        setSelectedDepartmentId(departmentId)
        if (departmentId && selectedDepartmentType) {
            fetchDoctorsByDepartment(selectedDepartmentType, departmentId)
        }
    }

    const handleDoctorChange = (doctorId) => {
        const practitioner = doctors.find(doc => doc.id === doctorId)
        setSelectedDoctorId(doctorId)
        setSelectedPractitioner(practitioner)
        setAvailableDays([])
        setSelectedDate(null)
        setSelectedTimeSlot(null)
        if (doctorId) {
            fetchDoctorAvailableTimes(doctorId)
        }
    }

    const handleBookingSubmit = async () => {
        if (!selectedDepartmentId || !selectedDoctorId || !selectedTimeSlot || !selectedPractitioner) {
            message.warning('กรุณาเลือกแผนก แพทย์ และช่วงเวลานัดหมาย')
            return
        }

        if (!selectedDate) {
            message.warning('กรุณาเลือกวันที่นัดหมาย')
            return
        }

        if (!patientId) {
            message.warning('ไม่พบข้อมูลผู้ป่วยในระบบ')
            return
        }

        setLoadingBooking(true)
        try {
            // คำนวณวันที่และเวลาจาก selectedTimeSlot
            const startOfSelectedWeek = moment(selectedDate).startOf('isoWeek')
            const selectedDateTime = moment(startOfSelectedWeek).add(selectedTimeSlot.value, 'minutes')
            const bookingData = {
                admitTime: selectedDateTime.toISOString(),
                bookingTime: selectedTimeSlot.value, // จำนวนนาทีนับจากเริ่มต้นสัปดาห์ (Monday 00:00)
                bookingCategory: 'scheduling',
                practitionerAppUserId: selectedPractitioner.appUserId,
                practitionerId: selectedPractitioner.id,
                doctorId: selectedDoctorId,
                organizationId: selectedPractitioner?.organizations?.[0]?.id || null,
                status: 'DOCTOR_PENDING',
                productableType: 'Telemedicine',
                patientId: patientId,
            }

            await httpEver.post('/Bookings', bookingData)

            message.success('สร้างการนัดหมายเรียบร้อยแล้ว')
            setBookingModalVisible(false)
            setBookingDate(null)
            setBookingTime(null)
            setBookingNotes('')
            setPatientId(null)
            setSelectedDepartmentType(null)
            setSelectedDepartmentId(null)
            setDoctors([])
            setSelectedDoctorId(null)
            setSelectedPractitioner(null)
            setAvailableDays([])
            setSelectedTimeSlot(null)
            fetchRegistrations(pagination.current, searchText)
        } catch (error) {
            console.error('Error creating booking:', error)
            message.error('เกิดข้อผิดพลาดในการสร้างการนัดหมาย')
        } finally {
            setLoadingBooking(false)
        }
    }

    const getStatusTag = (status) => {
        const statusMap = {
            pending: { color: 'warning', text: 'รอดำเนินการ' },
            approved: { color: 'success', text: 'อนุมัติแล้ว' },
            sent_to_pharmacy: { color: 'processing', text: 'ส่งไปยัง Pharmacy' },
            medicine_delivered: { color: 'purple', text: 'ส่งยาสำเร็จ' }
        }

        const statusInfo = statusMap[status] || { color: 'default', text: status }
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
    }

    const columns = [
        {
            title: 'ชื่อ-นามสกุล',
            dataIndex: 'firstname',
            key: 'name',
            render: (text, record) => `${record.firstname} ${record.lastname}`,
            width: 200,
        },
        {
            title: 'CID',
            dataIndex: 'cId',
            key: 'cId',
            width: 120,
        },
        {
            title: 'สถานะ',
            dataIndex: 'status',
            key: 'status',
            render: (status) => getStatusTag(status),
            width: 150,
        },
        {
            title: 'วันที่สมัคร',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => new Date(date).toLocaleDateString('th-TH'),
            width: 120,
        },
        {
            title: 'การดำเนินการ',
            key: 'action',
            render: (_, record) => (
                <Space size="small">
                    {record.status === 'pending' && (
                        <Tooltip title="กรอกข้อมูลสมัครให้ครบ">
                            <Button
                                type="primary"
                                icon={<UserAddOutlined />}
                                size="small"
                                style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                                onClick={() => handleCompleteRegistration(record)}
                            />
                        </Tooltip>
                    )}
                    {record.status === 'approved' && (
                        <Tooltip title="ส่งไปยัง Pharmacy Emergency">
                            <Button
                                type="primary"
                                icon={<MedicineBoxOutlined />}
                                size="small"
                                style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                                onClick={() => handleSendToPharmacy(record)}
                            />
                        </Tooltip>
                    )}
                    {record.status === 'medicine_delivered' && (
                        <Tooltip title="นัดหมายผู้ป่วย">
                            <Button
                                type="primary"
                                icon={<CalendarOutlined />}
                                size="small"
                                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                onClick={() => handleCreateBooking(record)}
                            />
                        </Tooltip>
                    )}
                </Space>
            ),
            width: 150,
        },
    ]

    const validationSchema = Yup.object().shape({
        firstname: Yup.string().required('กรุณากรอกชื่อ'),
        lastname: Yup.string().required('กรุณากรอกนามสกุล'),
        cId: Yup.string()
            .min(13, 'กรุณากรอกเลขบัตรประชาชนให้ครบ 13 หลัก')
            .required('กรุณากรอกเลขบัตรประชาชน'),
        mobileNumber: Yup.string()
            .min(10, 'กรุณากรอกเบอร์มือถือให้ครบ 10 หลัก')
            .required('กรุณากรอกเบอร์มือถือ'),
        day: Yup.number()
            .max(31, 'วันที่ไม่ควรเกิน 31')
            .required('กรุณากรอกวันที่เกิด'),
        month: Yup.number()
            .max(12, 'เดือนไม่ควรเกิน 12')
            .required('กรุณากรอกเดือนเกิด'),
        year: Yup.number()
            .min(2400, 'กรุณากรอก พ.ศ. เกิดที่ถูกต้อง')
            .max(new Date().getFullYear() + 543, 'กรุณากรอก พ.ศ. เกิดที่ถูกต้อง')
            .required('กรุณากรอกปีเกิด'),
        password: Yup.string()
            .min(6, 'รหัสผ่านไม่ควรต่ำกว่า 6 ตัวอักษร')
            .required('กรุณากรอกรหัสผ่าน'),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('password')], 'รหัสผ่านและยืนยันรหัสผ่านไม่ตรงกัน')
            .required('กรุณายืนยันรหัสผ่าน'),
        address: Yup.string().required('กรุณากรอกที่อยู่')
    })

    return (
        <Layout
            {...props}
            render={(layoutProps) => (
                <Container>
                    <div className="header-section">
                        <h2>รายการสมัครผู้ป่วย</h2>
                    </div>

                    <div className="search-section">
                        <Input.Search
                            placeholder="ค้นหาด้วยชื่อ, นามสกุล, หรือ HN ID"
                            allowClear
                            enterButton={<SearchOutlined />}
                            size="large"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onSearch={handleSearch}
                            style={{ maxWidth: 400 }}
                        />
                    </div>

                    <div className="table-container">
                        <Table
                            columns={columns}
                            dataSource={registrations}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                ...pagination,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} จาก ${total} รายการ`,
                            }}
                            onChange={handleTableChange}
                            scroll={{ x: 800 }}
                        />
                    </div>

                    {/* Complete Registration Modal */}
                    <Modal
                        title="กรอกข้อมูลการสมัครให้ผู้ป่วย"
                        open={completeRegistrationModalVisible}
                        onCancel={() => {
                            setCompleteRegistrationModalVisible(false)
                            setPatientDataFromHIE(null)
                            setSelectedProvince('')
                            setSelectedAmphoe('')
                            setSelectedDistrict('')
                            setPostalCode('')
                        }}
                        footer={null}
                        width={800}
                    >
                        {selectedRecord && (
                            <Spin spinning={loadingPatientData}>
                                <Formik
                                    enableReinitialize
                                    initialValues={{
                                        firstname: patientDataFromHIE?.data?.fullname?.firstName || selectedRecord.firstname || '',
                                        lastname: patientDataFromHIE?.data?.fullname?.lastName || selectedRecord.lastname || '',
                                        hnId: getFormattedHN(patientDataFromHIE?.data?.hn || patientDataFromHIE?.hn) || selectedRecord.hnId || '',
                                        cId: patientDataFromHIE?.data?.cid || patientDataFromHIE?.cid || '',
                                        mobileNumber: patientDataFromHIE?.data?.contactInfo?.mobileNumber || '',
                                        gender: patientDataFromHIE?.data ? convertGender(patientDataFromHIE.data.sex, patientDataFromHIE.data.sexName) : 'male',
                                        day: patientDataFromHIE?.data?.DOB ? convertDateToBE(patientDataFromHIE.data.DOB).day : '',
                                        month: patientDataFromHIE?.data?.DOB ? convertDateToBE(patientDataFromHIE.data.DOB).month : '',
                                        year: patientDataFromHIE?.data?.DOB ? convertDateToBE(patientDataFromHIE.data.DOB).year : '',
                                        password: '',
                                        confirmPassword: '',
                                        address: patientDataFromHIE?.data?.address?.addressPart || ''
                                    }}
                                    validationSchema={validationSchema}
                                    onSubmit={handleCompleteRegistrationSubmit}
                                >
                                    {({ values, handleChange, handleSubmit, errors, touched, setFieldValue }) => (
                                        <Form layout="vertical">
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                                <Form.Item label="ชื่อ" required>
                                                    <Input
                                                        value={values.firstname}
                                                        onChange={handleChange('firstname')}
                                                        placeholder="ชื่อ"
                                                    />
                                                    {touched.firstname && errors.firstname && (
                                                        <div style={{ color: 'red', fontSize: '12px' }}>{errors.firstname}</div>
                                                    )}
                                                </Form.Item>

                                                <Form.Item label="นามสกุล" required>
                                                    <Input
                                                        value={values.lastname}
                                                        onChange={handleChange('lastname')}
                                                        placeholder="นามสกุล"
                                                    />
                                                    {touched.lastname && errors.lastname && (
                                                        <div style={{ color: 'red', fontSize: '12px' }}>{errors.lastname}</div>
                                                    )}
                                                </Form.Item>

                                                <Form.Item label="HN ID" required>
                                                    <Input
                                                        value={values.hnId}
                                                        placeholder="HN ID (เช่น 28318/66)"
                                                        disabled
                                                        readOnly
                                                    />
                                                </Form.Item>

                                                <Form.Item label="เลขบัตรประชาชน" required>
                                                    <Input
                                                        value={values.cId}
                                                        onChange={handleChange('cId')}
                                                        placeholder="เลขบัตรประชาชน 13 หลัก"
                                                        maxLength={13}
                                                    />
                                                    {touched.cId && errors.cId && (
                                                        <div style={{ color: 'red', fontSize: '12px' }}>{errors.cId}</div>
                                                    )}
                                                </Form.Item>

                                                <Form.Item label="เบอร์มือถือ" required>
                                                    <Input
                                                        value={values.mobileNumber}
                                                        onChange={handleChange('mobileNumber')}
                                                        placeholder="เบอร์มือถือ 10 หลัก"
                                                        maxLength={10}
                                                    />
                                                    {touched.mobileNumber && errors.mobileNumber && (
                                                        <div style={{ color: 'red', fontSize: '12px' }}>{errors.mobileNumber}</div>
                                                    )}
                                                </Form.Item>

                                                <Form.Item label="เพศ" required>
                                                    <Radio.Group
                                                        value={values.gender}
                                                        onChange={(e) => setFieldValue('gender', e.target.value)}
                                                    >
                                                        <Radio value="male">ชาย</Radio>
                                                        <Radio value="female">หญิง</Radio>
                                                    </Radio.Group>
                                                </Form.Item>

                                                <Form.Item label="วันเกิด" required>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <Input
                                                            placeholder="วัน"
                                                            value={values.day}
                                                            onChange={handleChange('day')}
                                                            maxLength={2}
                                                            style={{ width: '60px' }}
                                                        />
                                                        <Input
                                                            placeholder="เดือน"
                                                            value={values.month}
                                                            onChange={handleChange('month')}
                                                            maxLength={2}
                                                            style={{ width: '60px' }}
                                                        />
                                                        <Input
                                                            placeholder="ปี พ.ศ."
                                                            value={values.year}
                                                            onChange={handleChange('year')}
                                                            maxLength={4}
                                                            style={{ width: '100px' }}
                                                        />
                                                    </div>
                                                    {(touched.day && errors.day) || (touched.month && errors.month) || (touched.year && errors.year) && (
                                                        <div style={{ color: 'red', fontSize: '12px' }}>
                                                            {errors.day || errors.month || errors.year}
                                                        </div>
                                                    )}
                                                </Form.Item>

                                                <Form.Item label="รหัสผ่าน" required>
                                                    <Input.Password
                                                        value={values.password}
                                                        onChange={handleChange('password')}
                                                        placeholder="รหัสผ่าน"
                                                    />
                                                    {touched.password && errors.password && (
                                                        <div style={{ color: 'red', fontSize: '12px' }}>{errors.password}</div>
                                                    )}
                                                </Form.Item>

                                                <Form.Item label="ยืนยันรหัสผ่าน" required>
                                                    <Input.Password
                                                        value={values.confirmPassword}
                                                        onChange={handleChange('confirmPassword')}
                                                        placeholder="ยืนยันรหัสผ่าน"
                                                    />
                                                    {touched.confirmPassword && errors.confirmPassword && (
                                                        <div style={{ color: 'red', fontSize: '12px' }}>{errors.confirmPassword}</div>
                                                    )}
                                                </Form.Item>
                                            </div>

                                            <Form.Item label="ที่อยู่" required>
                                                <Input
                                                    value={values.address}
                                                    onChange={handleChange('address')}
                                                    placeholder="เลขที่ / ถนน / อาคาร"
                                                />
                                                {touched.address && errors.address && (
                                                    <div style={{ color: 'red', fontSize: '12px' }}>{errors.address}</div>
                                                )}
                                            </Form.Item>

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                                                <Form.Item label="จังหวัด" required>
                                                    <Select
                                                        value={selectedProvince}
                                                        onChange={handleProvinceChange}
                                                        placeholder="เลือกจังหวัด"
                                                    >
                                                        {provinces.map(province => (
                                                            <Option key={province} value={province}>{province}</Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>

                                                <Form.Item label="เขต/อำเภอ" required>
                                                    <Select
                                                        value={selectedAmphoe}
                                                        onChange={handleAmphoeChange}
                                                        placeholder="เลือกเขต/อำเภอ"
                                                        disabled={!selectedProvince}
                                                    >
                                                        {amphoes.map(amphoe => (
                                                            <Option key={amphoe} value={amphoe}>{amphoe}</Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>

                                                <Form.Item label="แขวง/ตำบล" required>
                                                    <Select
                                                        value={selectedDistrict}
                                                        onChange={handleDistrictChange}
                                                        placeholder="เลือกแขวง/ตำบล"
                                                        disabled={!selectedAmphoe}
                                                    >
                                                        {districts.map(district => (
                                                            <Option key={district} value={district}>{district}</Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </div>

                                            <Form.Item label="รหัสไปรษณีย์">
                                                <Input
                                                    value={postalCode}
                                                    placeholder="รหัสไปรษณีย์"
                                                    disabled
                                                />
                                            </Form.Item>

                                            <div style={{ textAlign: 'right', marginTop: '20px' }}>
                                                <Space>
                                                    <Button onClick={() => setCompleteRegistrationModalVisible(false)}>
                                                        ยกเลิก
                                                    </Button>
                                                    <Button type="primary" onClick={handleSubmit}>
                                                        สมัครผู้ป่วย
                                                    </Button>
                                                </Space>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </Spin>
                        )}
                    </Modal>

                    {/* Send to Pharmacy Modal */}
                    <Modal
                        title="ส่งผู้ป่วยไปยัง Pharmacy Emergency"
                        open={sendToPharmacyModalVisible}
                        onOk={handleSendToPharmacySubmit}
                        onCancel={() => setSendToPharmacyModalVisible(false)}
                        okText="ส่งไปยัง Pharmacy"
                        cancelText="ยกเลิก"
                        okButtonProps={{ style: { backgroundColor: '#722ed1', borderColor: '#722ed1' } }}
                    >
                        <p>คุณต้องการส่งผู้ป่วย <strong>{selectedRecord?.firstname} {selectedRecord?.lastname}</strong> ไปยัง Pharmacy Emergency หรือไม่?</p>
                        <TextArea
                            placeholder="หมายเหตุสำหรับ Pharmacy (ไม่บังคับ)"
                            value={pharmacyNotes}
                            onChange={(e) => setPharmacyNotes(e.target.value)}
                            rows={3}
                        />
                    </Modal>

                    {/* Create Booking Modal */}
                    <Modal
                        title="นัดหมายผู้ป่วย"
                        open={bookingModalVisible}
                        onOk={handleBookingSubmit}
                        onCancel={() => {
                            setBookingModalVisible(false)
                            setBookingDate(null)
                            setBookingTime(null)
                            setBookingNotes('')
                            setPatientId(null)
                            setSelectedDepartmentType(null)
                            setSelectedDepartmentId(null)
                            setDoctors([])
                            setSelectedDoctorId(null)
                            setAvailableDays([])
                            setSelectedDate(null)
                            setSelectedTimeSlot(null)
                        }}
                        okText="สร้างการนัดหมาย"
                        cancelText="ยกเลิก"
                        okButtonProps={{
                            style: { backgroundColor: '#52c41a', borderColor: '#52c41a' },
                            loading: loadingBooking
                        }}
                        width={700}
                        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
                    >
                        {selectedRecord && (
                            <Spin spinning={loadingDepartments || loadingDoctors || loadingTimeSlots}>
                                <Form layout="vertical">
                                    <Form.Item label="ผู้ป่วย">
                                        <Input
                                            value={`${selectedRecord.firstname} ${selectedRecord.lastname}`}
                                            disabled
                                        />
                                    </Form.Item>
                                    <Form.Item label="CID">
                                        <Input
                                            value={selectedRecord.cId}
                                            disabled
                                        />
                                    </Form.Item>

                                    <Form.Item label="ประเภทแผนก" required>
                                        <Radio.Group
                                            value={selectedDepartmentType}
                                            onChange={(e) => handleDepartmentTypeChange(e.target.value)}
                                        >
                                            <Radio value="doctorTypes">แพทย์</Radio>
                                            <Radio value="otherTypes">บุคลากรอื่นๆ</Radio>
                                        </Radio.Group>
                                    </Form.Item>

                                    <Form.Item label="แผนก" required>
                                        <Select
                                            style={{ width: '100%' }}
                                            value={selectedDepartmentId}
                                            onChange={handleDepartmentChange}
                                            placeholder="เลือกแผนก"
                                            disabled={!selectedDepartmentType}
                                            loading={loadingDepartments}
                                        >
                                            {selectedDepartmentType === 'doctorTypes' && doctorTypes.map((dept) => (
                                                <Option key={dept.id} value={dept.id}>
                                                    {dept.name || dept.type}
                                                </Option>
                                            ))}
                                            {selectedDepartmentType === 'otherTypes' && otherTypes.map((dept) => (
                                                <Option key={dept.id} value={dept.id}>
                                                    {dept.name || dept.type}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item label="แพทย์/บุคลากร" required>
                                        <Select
                                            style={{ width: '100%' }}
                                            value={selectedDoctorId}
                                            onChange={handleDoctorChange}
                                            placeholder="เลือกแพทย์/บุคลากร"
                                            disabled={!selectedDepartmentId}
                                            loading={loadingDoctors}
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                option.children.toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {doctors.map((doctor) => (
                                                <Option key={doctor.id} value={doctor.id}>
                                                    {doctor.fullname || doctor.email}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item label="เลือกวันที่นัดหมาย" required>
                                        {availableDays.length > 0 ? (
                                            <DatePicker
                                                style={{ width: '100%' }}
                                                value={selectedDate}
                                                onChange={(date) => {
                                                    setSelectedDate(date)
                                                    setSelectedTimeSlot(null)
                                                }}
                                                format="DD/MM/YYYY"
                                                placeholder="เลือกวันที่นัดหมาย"
                                                disabledDate={(current) => {
                                                    if (!current) return false

                                                    // ไม่อนุญาตให้เลือกวันที่ในอดีต
                                                    if (current < moment().startOf('day')) return true

                                                    // ตรวจสอบว่าวันที่เลือกตรงกับวันที่มีเวลาว่าง (dayOfWeek)
                                                    const selectedDayOfWeek = current.day() === 0 ? 6 : current.day() - 1 // 0-6 (Monday = 0)

                                                    // ตรวจสอบว่ามีวันนี้ใน availableDays หรือไม่
                                                    const isAvailable = availableDays.some(day => {
                                                        return day.dayOfWeek === selectedDayOfWeek
                                                    })

                                                    return !isAvailable
                                                }}
                                            />
                                        ) : selectedDoctorId ? (
                                            <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                                {loadingTimeSlots ? 'กำลังโหลด...' : 'ไม่มีช่วงเวลาว่าง'}
                                            </div>
                                        ) : (
                                            <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                                กรุณาเลือกแพทย์/บุคลากร
                                            </div>
                                        )}
                                    </Form.Item>

                                    <Form.Item label="ช่วงเวลาว่าง" required>
                                        {selectedDate && availableDays.length > 0 ? (
                                            (() => {
                                                // หาวันที่ที่เลือกจาก availableDays โดยใช้ dayOfWeek
                                                const selectedDayOfWeek = selectedDate.day() === 0 ? 6 : selectedDate.day() - 1 // 0-6 (Monday = 0)
                                                const selectedDayData = availableDays.find(day => {
                                                    return day.dayOfWeek === selectedDayOfWeek
                                                })

                                                if (selectedDayData && selectedDayData.timePeriods && selectedDayData.timePeriods.length > 0) {
                                                    // แบ่งเวลาออกเป็นช่วงเช้า บ่าย เย็น
                                                    const morningTimes = selectedDayData.timePeriods.filter(time => {
                                                        const timeStr = moment().startOf('isoWeek').add(time, 'minutes').format('HH:mm')
                                                        return timeStr < '12:00'
                                                    })

                                                    const afternoonTimes = selectedDayData.timePeriods.filter(time => {
                                                        const timeStr = moment().startOf('isoWeek').add(time, 'minutes').format('HH:mm')
                                                        return timeStr >= '12:00' && timeStr < '18:00'
                                                    })

                                                    const eveningTimes = selectedDayData.timePeriods.filter(time => {
                                                        const timeStr = moment().startOf('isoWeek').add(time, 'minutes').format('HH:mm')
                                                        return timeStr >= '18:00'
                                                    })

                                                    const renderTimeButtons = (times, periodName) => {
                                                        if (times.length === 0) return null

                                                        return (
                                                            <div key={periodName} style={{ marginBottom: '20px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                                                    <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#666' }}>
                                                                        {periodName === 'morning' ? 'เช้า' : periodName === 'afternoon' ? 'บ่าย' : 'เย็น'}
                                                                    </span>
                                                                    <Tag color="blue" style={{ marginLeft: '10px' }}>
                                                                        {times.length} Slots
                                                                    </Tag>
                                                                </div>
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                                    {times.map((time, timeIndex) => {
                                                                        const timeLabel = moment().startOf('isoWeek').add(time, 'minutes').format('HH:mm')
                                                                        const isSelected = selectedTimeSlot?.value === time

                                                                        return (
                                                                            <Button
                                                                                key={timeIndex}
                                                                                type={isSelected ? 'primary' : 'default'}
                                                                                onClick={() => setSelectedTimeSlot({
                                                                                    value: time,
                                                                                    label: timeLabel,
                                                                                    day: selectedDate.date(),
                                                                                    date: selectedDate.format('dddd'),
                                                                                    dateValue: selectedDate.format('YYYY-MM-DD')
                                                                                })}
                                                                                style={{
                                                                                    backgroundColor: isSelected ? '#52c41a' : '#f0f0f0',
                                                                                    borderColor: isSelected ? '#52c41a' : '#d9d9d9',
                                                                                    color: isSelected ? 'white' : 'black',
                                                                                    minWidth: '80px'
                                                                                }}
                                                                            >
                                                                                {timeLabel}
                                                                            </Button>
                                                                        )
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )
                                                    }

                                                    return (
                                                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                            {renderTimeButtons(morningTimes, 'morning')}
                                                            {renderTimeButtons(afternoonTimes, 'afternoon')}
                                                            {renderTimeButtons(eveningTimes, 'evening')}
                                                        </div>
                                                    )
                                                } else {
                                                    return (
                                                        <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                                            ไม่มีช่วงเวลาว่างในวันที่เลือก
                                                        </div>
                                                    )
                                                }
                                            })()
                                        ) : (
                                            <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                                กรุณาเลือกวันที่นัดหมายก่อน
                                            </div>
                                        )}
                                    </Form.Item>

                                    {selectedTimeSlot && (
                                        <Form.Item>
                                            <div style={{
                                                padding: '12px',
                                                backgroundColor: '#e6f7ff',
                                                borderRadius: '4px',
                                                border: '1px solid #91d5ff'
                                            }}>
                                                <strong>เลือกแล้ว:</strong> {selectedTimeSlot.date} วันที่ {selectedTimeSlot.day} เวลา {selectedTimeSlot.label}
                                            </div>
                                        </Form.Item>
                                    )}

                                    <Form.Item label="หมายเหตุ (ไม่บังคับ)">
                                        <TextArea
                                            placeholder="หมายเหตุสำหรับการนัดหมาย"
                                            value={bookingNotes}
                                            onChange={(e) => setBookingNotes(e.target.value)}
                                            rows={3}
                                        />
                                    </Form.Item>
                                </Form>
                            </Spin>
                        )}
                    </Modal>
                </Container>
            )}
        />
    )
}

export default PatientRegistrationList